// src/components/tables/OpportunityTeamTable.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { apiFetch } from '../../utils/api';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableRow,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import ChatIcon from '@mui/icons-material/Chat';
import CallIcon from '@mui/icons-material/Call';
import { alpha } from '@mui/material/styles';
import { Logger } from '../../utils/logger';
import EmployeeNameLink from '../common/EmployeeNameLink';
import { TABS } from '../cards/EmployeeExpandedView';
import CardSectionLabel from '../cards/CardSectionLabel';

const logger = new Logger('OpportunityTeamTable');

/**
 * OpportunityTeamTable component displays team members assigned to an opportunity
 * in a tabular format with name, role, and contact options.
 */
const OpportunityTeamTable = ({ 
  team = [], 
  opportunityId = null,
  limit = 5, // Default to 5 team members
  showMore = false, 
  onShowMoreClick 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Fetch team members from API if not provided as props
  useEffect(() => {
    if (team && team.length > 0) {
      // Use the team provided via props
      setTeamMembers(team);
      return;
    }
    
    if (!opportunityId) {
      // If no opportunity ID and no team, nothing to show
      return;
    }
    
    // Fetch team members from API
    const fetchTeamMembers = async () => {
      setLoading(true);
      setError(null);

      try {
        logger.debug(`Fetching team members for opportunity ID: ${opportunityId}`);

        const response = await apiFetch(`opportunities/${opportunityId}/team`);
        if (response.ok) {
          const data = await response.json();
          setTeamMembers(Array.isArray(data) ? data : []);
        } else {
          logger.warn('Non-OK response fetching team members:', response.status);
          setTeamMembers([]);
        }
      } catch (err) {
        logger.error('Error fetching team members:', err);
        setError('Failed to load team members');
        setTeamMembers([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeamMembers();
  }, [opportunityId, team, limit]);
  
  // Communication handler functions
  const handleEmail = (e, email, name) => {
    e.stopPropagation();
    if (email) {
      window.location.href = `mailto:${email}`;
    } else {
      logger.debug(`No email available for ${name}`);
    }
  };
  
  const handleChat = (e, employeeId, name) => {
    e.stopPropagation();
    if (employeeId) {
      // Construct Teams chat URL with employee email
      // This is a placeholder - actual implementation depends on your system
      const email = `${employeeId.toLowerCase()}@mbco.com`;
      window.open(`https://teams.microsoft.com/l/chat/0/0?users=${email}`, '_blank');
    } else {
      logger.debug(`Cannot chat with ${name} - no employee ID`);
    }
  };
  
  const handleCall = (e, phone, name) => {
    e.stopPropagation();
    if (phone) {
      window.location.href = `tel:${phone}`;
    } else {
      logger.debug(`No phone number available for ${name}`);
    }
  };
  
  // Show loading state
  if (loading) {
    return (
      <Box sx={{ px: 1.5, pt: 1.5 }}>
        <CardSectionLabel label="Pursuit Team Members" />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={20} />
        </Box>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ px: 1.5, pt: 1.5 }}>
        <CardSectionLabel label="Pursuit Team Members" />
        <Typography color="error" sx={{ fontSize: '0.7rem' }}>
          {error}
        </Typography>
      </Box>
    );
  }

  const displayedTeam = teamMembers.slice(0, limit);
  const hasMoreMembers = teamMembers.length > limit;

  return (
    <Box sx={{ px: 1.5, pt: 1.5 }}>
      <CardSectionLabel
        label="Pursuit Team Members"
        action={(showMore || hasMoreMembers) && onShowMoreClick ? (
          <Typography
            onClick={onShowMoreClick}
            sx={{ fontSize: '0.65rem', color: 'primary.main', cursor: 'pointer' }}
          >
            View All
          </Typography>
        ) : null}
      />
      
      <Table size="small" sx={{ mb: 0.5, backgroundColor: '#1A1A1A' }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#1A1A1A' }}>
            <TableCell>Name</TableCell>
            <TableCell>Role</TableCell>
            <TableCell align="right">Contact</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedTeam.length > 0 ? (
            displayedTeam.map((member, index) => (
              <TableRow key={index} sx={{ backgroundColor: '#1A1A1A' }}>
                <TableCell sx={{ backgroundColor: '#1A1A1A' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography sx={{ color: 'text.primary', fontSize: '0.75rem' }}>
                      <EmployeeNameLink employee={member} tab={TABS.ENGAGEMENTS}>{member.name}</EmployeeNameLink>
                    </Typography>
                    {member.employee_id && (
                      <Typography sx={{ color: 'text.disabled', fontSize: '0.6rem' }}>
                        {member.employee_id}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A' }}>
                  {member.role}
                </TableCell>
                <TableCell sx={{ backgroundColor: '#1A1A1A', textAlign: 'right' }}>
                  {member.is_terminated ? (
                    <Chip
                      label="Terminated"
                      size="small"
                      sx={{
                        bgcolor: alpha('#CF6679', 0.15),
                        color: '#CF6679',
                        fontSize: '0.65rem',
                        height: 20
                      }}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Tooltip title={member.email ? `Email ${member.name.split(' ')[0]}` : `No email available`}>
                        <IconButton 
                          size="small" 
                          sx={{ 
                            color: member.email ? '#64B5F6' : 'rgba(100, 181, 246, 0.5)',
                            p: 0.5 
                          }}
                          onClick={(e) => handleEmail(e, member.email, member.name)}
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={`Chat with ${member.name.split(' ')[0]}`}>
                        <IconButton 
                          size="small" 
                          sx={{ color: '#BB86FC', p: 0.5 }}
                          onClick={(e) => handleChat(e, member.employee_id, member.name)}
                        >
                          <ChatIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={member.phone ? `Call ${member.name.split(' ')[0]}` : `No phone number available`}>
                        <IconButton 
                          size="small" 
                          sx={{ 
                            color: member.phone ? '#03DAC6' : 'rgba(3, 218, 198, 0.5)',
                            p: 0.5
                          }}
                          onClick={(e) => handleCall(e, member.phone, member.name)}
                        >
                          <CallIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow sx={{ backgroundColor: '#1A1A1A' }}>
              <TableCell colSpan={3} align="center" sx={{ backgroundColor: '#1A1A1A' }}>
                No team members assigned yet
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

OpportunityTeamTable.propTypes = {
  team: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    role: PropTypes.string,
    employee_id: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    is_terminated: PropTypes.bool
  })),
  opportunityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  limit: PropTypes.number,
  showMore: PropTypes.bool,
  onShowMoreClick: PropTypes.func
};

export default OpportunityTeamTable;
